import {
  Button,
  ColumnProps,
  Switch,
  Table,
  TableProps,
  useTheme,
} from "@monolith-forensics/monolith-ui";
import { DocumentTemplate } from "../DocumentTemplates.types";
import { Trash2Icon } from "lucide-react";
import { ActionMenu } from "../../Tables";
import { DocumentTemplateColumnDefinitions } from "../Definitions";
import { useAuth } from "@/contexts/AuthContext";

export enum DataFields {
  ACTION = "Action",
  UUID = "item_id",
  NAME = "person_name",
  TYPE = "person_type",
  UNIQUE_ID = "unique_id",
}

export const DocumentTemplatesTable: React.FC<{
  data: any[];
  readOnly?: boolean;
  onAdd?: () => void;
  onEdit?: (item: DocumentTemplate) => void;
  onUpdate?: (item: Partial<DocumentTemplate>) => void;
  onDelete?: (item: DocumentTemplate) => void;
  onDownload?: (item: DocumentTemplate) => void;
  excludedColumns?: string[];
  showDelete?: boolean;
  tableMenuOptions?: TableProps["tableMenuOptions"];
  loading?: boolean;
  stateStoreKey?: string;
}> = ({
  data,
  readOnly,
  onAdd,
  onEdit,
  onUpdate,
  onDelete,
  onDownload,
  showDelete,
  excludedColumns = [],
  tableMenuOptions = {},
  loading,
  stateStoreKey = "document-templates:table",
}) => {
  const { currentUser } = useAuth();
  const theme = useTheme();
  const columnProps: ColumnProps[] = [];

  if (showDelete) {
    columnProps.push({
      dataField: "Delete",
      width: 50,
      render: ({ rowData }: { rowData: DocumentTemplate }) => {
        return (
          <Button
            variant="subtle"
            size="xxs"
            onClick={() => onDelete?.(rowData)}
          >
            <Trash2Icon size={16} color="orangered" />
          </Button>
        );
      },
    });
  }

  if (!excludedColumns.includes(DataFields.ACTION)) {
    columnProps.push({
      dataField: "Action",
      width: 50,
      render: ({ rowData }: { rowData: DocumentTemplate }) => {
        return (
          <ActionMenu
            item={rowData}
            onEdit={readOnly ? undefined : () => onEdit?.(rowData)}
            onDelete={readOnly ? undefined : () => onDelete?.(rowData)}
            onDownload={() => onDownload?.(rowData)}
          />
        );
      },
    });
  }

  columnProps.push(
    ...DocumentTemplateColumnDefinitions.filter(
      (col) => !excludedColumns?.includes(col.dataField)
    )
  );

  columnProps.push({
    dataField: "is_shared",
    caption: "Shared",
    render: ({ rowData }: { rowData: DocumentTemplate }) => {
      const isOwned = currentUser.user_id === rowData.created_by?.user_id;
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: 10,
            alignItems: "center",
            justifyContent: "flex-end",
            fontWeight: 600,
            color: theme.palette.text.secondary,
            width: "100%",
            userSelect: "none",
          }}
        >
          <div>Shared</div>
          <Switch
            size="xs"
            disabled={!isOwned}
            value={rowData.is_shared}
            onChange={(value) => {
              onUpdate?.({
                uuid: rowData.uuid,
                is_shared: value,
              });
            }}
          />
        </div>
      );
    },
  });

  return (
    <Table
      columnProps={columnProps}
      data={data}
      keyField="uuid"
      loading={loading}
      enableSorting={true}
      stateStorage={{
        enabled: !!stateStoreKey,
        type: "localStorage",
        key: stateStoreKey,
      }}
      tableMenuOptions={{
        enabled: true,
        addButtonOptions: {
          enabled: !readOnly,
          label: "+ Add Template",
          onClick: () => onAdd?.(),
        },
        tableCountOptions: {
          recordsTotalEnabled: true,
        },
        columnSelectorOptions: {
          enabled: true,
        },
        searchOptions: {
          enabled: true,
        },
        ...tableMenuOptions,
      }}
    />
  );
};
